import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '@/services/api/apiClient';

export interface User {
  _id: string;
  name: string;
  empId: string;
  companyId?: {
    _id: string;
    name: string;
  } | string;
  role: 'admin' | 'manager' | 'employee';
  email: string;
  mobile: string;
  isActive: boolean;
  isDeleted: boolean;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserFormData {
  name: string;
  empId: string;
  companyId?: string;
  role: 'admin' | 'manager' | 'employee';
  email: string;
  mobile: string;
  password?: string;
  confirmPassword?: string;
  isActive?: boolean;
  remarks?: string;
}

export interface UserListParams {
  page?: number;
  limit?: number;
  role?: string;
  isActive?: boolean;
  companyId?: string;
}

export interface UserListResponse {
  success: boolean;
  message: string;
  data: {
    users: User[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface UserResponse {
  success: boolean;
  message: string;
  data: User;
}

export interface StatusUpdateData {
  isActive?: boolean;
  isDeleted?: boolean;
}

export interface PasswordUpdateData {
  password: string;
}

class UserService {
  private baseUrl = '/users';

  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.role) queryParams.append('role', params.role);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.companyId) queryParams.append('companyId', params.companyId);

    const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiGet<UserListResponse>(url);
  }

  async createUser(data: UserFormData): Promise<UserResponse> {
    return apiPost<UserResponse>(this.baseUrl, data);
  }

  async updateUser(id: string, data: UserFormData): Promise<UserResponse> {
    return apiPut<UserResponse>(`${this.baseUrl}/${id}`, data);
  }

  async deleteUser(id: string): Promise<{ success: boolean; message: string }> {
    return apiDelete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`);
  }

  async getUserById(id: string): Promise<UserResponse> {
    return apiGet<UserResponse>(`${this.baseUrl}/${id}`);
  }

  async updateUserStatus(id: string, data: StatusUpdateData): Promise<UserResponse> {
    return apiPatch<UserResponse>(`${this.baseUrl}/${id}/status`, data);
  }

  async updateUserPassword(id: string, data: PasswordUpdateData): Promise<{ success: boolean; message: string }> {
    return apiPatch<{ success: boolean; message: string }>(`${this.baseUrl}/${id}/password`, data);
  }

  async restoreUser(id: string): Promise<UserResponse> {
    return apiPatch<UserResponse>(`${this.baseUrl}/${id}/restore`, {});
  }
}

export const userService = new UserService();
